import { Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import * as SubframeCore from "@subframe/core";
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { ESCALATION_FOLLOW_UP_OUTCOME } from "modules/Escalation/constants/constants";

import { TextArea } from "subframe";
import { Button } from "subframe/components/Button";
import { Avatar } from "subframe/components/Avatar";
import { RadioCardGroup } from "subframe/components/RadioCardGroup";

import { getAcronym, isEmpty as isStringEmpty } from "../../../../utils/string";

import {
  followUpEscalation,
  updateFollowUpEscalation,
} from "modules/Escalation/services/escalation";

import "./EscalationFollowUp.css";
import { IconWithBackground } from "subframe";

function EscalationFollowUp() {
  const { id: escalationId } = useParams();
  const [searchParams] = useSearchParams();

  const [notes, setNotes] = useState("");
  const [outcome, setOutcome] = useState(
    ESCALATION_FOLLOW_UP_OUTCOME.OTHER.value
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [employeeId] = useState(+searchParams.get("employee_id"));
  const [jobTitle] = useState(searchParams.get("job_title") || "");
  const [escalationFollowUpId, setEscalationFollowUpId] = useState();
  const [supervisor] = useState(searchParams.get("supervisor") || "");
  const [isErrorMessageShown, setIsErrorMessageShown] = useState(false);
  const [phoneNumber] = useState(searchParams.get("phone_number") || "");
  const [employeeName] = useState(searchParams.get("employee_name") || "");
  const [isSuccessMessageShown, setIsSuccessMessageShown] = useState(false);
  const [hasTriedFormSubmission, setHasTriedFormSubmission] = useState(false);
  const [officeLocation] = useState(searchParams.get("office_location") || "");

  useEffect(() => {
    handleEscalationCreateOnPageVisit();
  }, []);

  const isFormValid = !isStringEmpty(outcome) && !isStringEmpty(notes);

  const resetForm = () => {
    setOutcome(ESCALATION_FOLLOW_UP_OUTCOME.OTHER.value);
    setNotes("");
    setHasTriedFormSubmission(false);
  };

  /**
   * Submits the escalation follow-up form.
   *
   * @param {Object} payload
   * @returns {Promise}
   */
  const submitEscalationFollowUpAsync = async (payload) => {
    try {
      return await followUpEscalation(payload);
    } catch (error) {
      return { hasError: true, error };
    }
  };

  /**
   * Update the escalation follow-up.
   *
   * @param {Object} payload
   * @returns {Promise}
   */
  const updateEscalationFollowUpAsync = async (payload) => {
    try {
      return await updateFollowUpEscalation(payload);
    } catch (error) {
      return { hasError: true, error };
    }
  };

  /**
   * Create escalation follow up when user visits the page by clicking on I followed up button from the escalation email.
   */
  const handleEscalationCreateOnPageVisit = async () => {
    setIsSubmitting(true);
    const response = await submitEscalationFollowUpAsync({
      escalation_id: +escalationId,
      employee_id: employeeId,
      outcome,
      notes,
    });

    setIsSubmitting(false);

    if (response?.hasError) {
      setErrorMessage(
        `Error while submitting: ${response?.error?.response?.data?.message}`
      );
      setIsErrorMessageShown(true);

      return;
    }

    setEscalationFollowUpId(response?.data?.result?.escalation_follow_up_id);
    setSuccessMessage("Successfully logged follow-up.");
    setIsSuccessMessageShown(true);
  };

  /**
   * Handles the submission of the escalation follow-up.
   */
  const handleFormSubmit = async () => {
    setHasTriedFormSubmission(true);

    if (!isFormValid) {
      return;
    }

    setIsSubmitting(true);
    const response = await updateEscalationFollowUpAsync({
      escalation_follow_up_id: +escalationFollowUpId,
      outcome,
      notes,
    });

    setIsSubmitting(false);

    if (response?.hasError) {
      setErrorMessage(
        `Error while updating: ${response?.error?.response?.data?.message}`
      );
      setIsErrorMessageShown(true);

      return;
    }

    resetForm();
    setSuccessMessage("Successfully updated follow-up.");
    setIsSuccessMessageShown(true);
  };

  /**
   * Renders the error snackbar.
   *
   * @returns {Component}
   */
  const renderErrorSnackbar = () => (
    <Snackbar
      open={isErrorMessageShown}
      autoHideDuration={6000}
      onClose={() => setIsErrorMessageShown(false)}
    >
      <MuiAlert
        onClose={() => setIsErrorMessageShown(false)}
        severity="error"
        sx={{ width: "100%" }}
      >
        {errorMessage}
      </MuiAlert>
    </Snackbar>
  );

  /**
   * Renders the success snackbar.
   *
   * @returns {Component}
   */
  const renderSuccessSnackbar = () => (
    <Snackbar
      open={isSuccessMessageShown}
      autoHideDuration={6000}
      onClose={() => setIsSuccessMessageShown(false)}
    >
      <MuiAlert
        onClose={() => setIsSuccessMessageShown(false)}
        severity="success"
        sx={{ width: "100%" }}
      >
        {successMessage}
      </MuiAlert>
    </Snackbar>
  );

  return (
    <div className="follow-up-outer-wrapper">
      <div className="shrink-0 flex-col gap-6 pt-6 pr-12 pb-6 pl-12 items-start flex follow-up-main-wrapper">
        <div className="w-full gap-2 items-center flex">
          <SubframeCore.Icon
            className="text-brand-700 text-section-header font-section-header"
            name="FeatherMessagesSquare"
          />
          <span className="grow shrink-0 basis-0 w-full text-default-font text-subheader font-subheader">
            Log follow-up
          </span>
        </div>
        <div className="w-full rounded-xl flex-col gap-6 pt-6 pr-6 pb-6 pl-6 items-start flex bg-brand-50">
          <div className="w-full gap-2 items-center flex">
            <Avatar>{getAcronym(employeeName)}</Avatar>
            <span className="grow shrink-0 basis-0 w-full text-default-font text-section-header font-section-header">
              {employeeName}
            </span>
          </div>
          <div className="w-full gap-16 items-start flex">
            <div className="grow shrink-0 basis-0 w-full flex-col gap-0.5 items-start flex">
              <div className="gap-1 items-center flex">
                <SubframeCore.Icon
                  className="text-subtext-color text-label-bold font-label-bold"
                  name="FeatherBriefcase"
                />
                <span className="text-subtext-color text-label-bold font-label-bold">
                  Job Title
                </span>
              </div>
              <span className="text-default-font text-body-bold font-body-bold">
                {jobTitle}
              </span>
            </div>
            <div className="grow shrink-0 basis-0 w-full flex-col gap-0.5 items-start flex">
              <div className="gap-1 items-center flex">
                <SubframeCore.Icon
                  className="text-subtext-color text-label-bold font-label-bold"
                  name="FeatherMapPin"
                />
                <span className="text-subtext-color text-label-bold font-label-bold">
                  Location
                </span>
              </div>
              <span className="text-default-font text-body-bold font-body-bold">
                {officeLocation}
              </span>
            </div>
            <div className="grow shrink-0 basis-0 w-full flex-col gap-0.5 items-start flex">
              <div className="gap-1 items-center flex">
                <SubframeCore.Icon
                  className="text-subtext-color text-label-bold font-label-bold"
                  name="FeatherPhone"
                />
                <span className="text-subtext-color text-label-bold font-label-bold">
                  Phone Number
                </span>
              </div>
              <span className="text-default-font text-body-bold font-body-bold">
                {phoneNumber}
              </span>
            </div>
            <div className="grow shrink-0 basis-0 w-full flex-col gap-0.5 items-start flex">
              <div className="gap-1 items-center flex">
                <SubframeCore.Icon
                  className="text-subtext-color text-label-bold font-label-bold"
                  name="FeatherUser"
                />
                <span className="text-subtext-color text-label-bold font-label-bold">
                  Supervisor
                </span>
              </div>
              <span className="text-default-font text-body-bold font-body-bold">
                {supervisor}
              </span>
            </div>
          </div>
        </div>
        <div className="w-full flex-col gap-8 pt-2 pr-2 pb-2 pl-2 items-start flex">
          <div className="w-full flex-col gap-2 items-start flex">
            <span className="text-default-font text-body-bold font-body-bold">
              Follow-up outcome
            </span>
            <RadioCardGroup
              onValueChange={(value) => setOutcome(value)}
              className="w-full"
            >
              <div className="grow shrink-0 basis-0 w-full flex-col gap-2 items-start flex">
                {Object.keys(ESCALATION_FOLLOW_UP_OUTCOME)?.map(
                  (outcomeKey) => {
                    return (
                      <RadioCardGroup.RadioCard
                        className="flex-none h-auto w-full"
                        checked={
                          outcome ===
                          ESCALATION_FOLLOW_UP_OUTCOME[outcomeKey].value
                        }
                        label={ESCALATION_FOLLOW_UP_OUTCOME[outcomeKey].label}
                        icon={ESCALATION_FOLLOW_UP_OUTCOME[outcomeKey].icon}
                        value={ESCALATION_FOLLOW_UP_OUTCOME[outcomeKey].value}
                      >
                        <div className="gap-2 pr-2 items-center flex">
                          {
                            <IconWithBackground
                              size="Large"
                              icon={
                                ESCALATION_FOLLOW_UP_OUTCOME[outcomeKey].icon
                              }
                            />
                          }
                          <span className="text-default-font text-body-bold font-body-bold">
                            {ESCALATION_FOLLOW_UP_OUTCOME[outcomeKey].label}
                          </span>
                        </div>
                      </RadioCardGroup.RadioCard>
                    );
                  }
                )}
              </div>
            </RadioCardGroup>
          </div>
          <div className="w-full flex-col gap-2 items-start flex">
            <span className="text-default-font text-body-bold font-body-bold">
              Notes
            </span>
            <TextArea
              onChange={(event) => {
                setNotes(event?.target?.value);
              }}
              className="flex-none h-auto w-full"
              label=""
              helpText={
                hasTriedFormSubmission && isStringEmpty(notes)
                  ? "Notes is required"
                  : ""
              }
              error={hasTriedFormSubmission && isStringEmpty(notes)}
            >
              <TextArea.Input
                value={notes}
                placeholder="Add follow-up notes..."
              />
            </TextArea>
          </div>
        </div>
        <div className="w-full gap-2 items-center justify-end flex">
          {/* <Button variant="Neutral Tertiary" icon={null} rightIcon={null}>
            Skip
          </Button> */}
          <Button
            disabled={isSubmitting}
            loading={isSubmitting}
            onClick={handleFormSubmit}
          >
            Submit
          </Button>
        </div>
      </div>
      {renderErrorSnackbar()}
      {renderSuccessSnackbar()}
    </div>
  );
}

export default EscalationFollowUp;
